/* eslint-disable jsx-a11y/aria-proptypes */
import {
  Button,
  Flex,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerHeader,
  DrawerBody,
  DrawerFooter,
  FormControl,
  FormLabel,
  Input,
  IconButton,
  RadioGroup,
  Stack,
  Radio,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  HStack,
  Box,
  Checkbox,
} from "@chakra-ui/react";
import { Select as MultiSelect } from "chakra-react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DateRangeSelect from "components/elements/DateRangeSelect";

import { dollarFormatter, percentFormatter } from "pages/Underwriting/elements/LoanCalculator";
import { useContext, useState } from "react";
import { selectChakraStyles } from "utils/constants";
import CompanyFieldsPopover from "../CompanyFieldsPopover";
import { SelectOption } from "utils/types";
import SaveFilterButton from "./SaveFilterButton";
import { useTenant } from "modules/tenant/providers/tenant-provider";
import { LoansContext } from "providers/loans";
import FilterSlider from "./FilterSlider";
import SavedFilters from "./SavedFilters";
import colors from "utils/colors";
import { useLoanTypes } from "hooks/useLoanTypes";

const LoanFilters = () => {
  const { user } = useTenant();
  const { filters, setFilters } = useContext(LoansContext);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectAllStatus, setSelectAllStatus] = useState(false);
  const [selectAllTypes, setSelectAllTypes] = useState(false);
  const { loanTypes } = useLoanTypes();
  const [tabIndex, setTabIndex] = useState(0);

  const addCustomFilter = (field: any) => {
    setFilters({
      ...filters,
      [field.id]: {
        ...field,
        value: "",
      },
    });
  };

  const removeCustomFilter = (id: string) => {
    setFilters({
      ...filters,
      [id]: undefined,
    });
  };

  const setCustomFilter = (field: any, value: any) => {
    setFilters({
      ...filters,
      [field.id]: {
        ...filters[field.id],
        value,
      },
    });
  };

  const handleAllStatusSelect = () => {
    if (filters && filters.status && filters.status.length > 0) {
      setFilters({
        ...filters,
        status: [],
      });
    } else {
      setFilters({
        ...filters,
        status: user.company.statuses.map((statusValue: any) => ({
          label: statusValue.label,
          value: statusValue.id,
        })),
      });
    }
  };

  const handleAllTypesSelect = () => {
    if (filters && filters.loan_types && filters.loan_types.length > 0) {
      setFilters({
        ...filters,
        loan_types: [],
      });
    } else {
      setFilters({
        ...filters,
        loan_types: loanTypes.map((statusValue: any) => ({
          label: statusValue.label,
          value: statusValue.value,
        })),
      });
    }
  };

  const determineCheckedTypes = () => {
    if (filters && filters.loan_types && filters.loan_types.length === loanTypes.length) {
      return true;
    } else return false;
  };

  const determineCheckedStatus = () => {
    if (filters && filters.status && filters.status.length === user.company.statuses.length) {
      return true;
    } else return false;
  };

  const handleCancel = () => {
    setFilters({});
    setSelectAllStatus(false);
    setSelectAllTypes(false);
  };

  return (
    <Box>
      <Button
        variant="outline"
        color="gray.200"
        borderColor="#D4D4D4"
        background="#fff"
        onClick={onOpen}
        leftIcon={<FontAwesomeIcon icon={["far", "filter"]} />}
      >
        {Object.keys(filters).length === 0
          ? "All Loans"
          : `Filters (${Object.keys(filters).length})`}
      </Button>
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent overflow="visible">
          <DrawerCloseButton />
          <DrawerHeader pb="0px">Filters</DrawerHeader>
          <DrawerBody overflowY="auto" maxH="calc(100vh - 200px)">
            <Tabs variant="enclosed" onChange={index => setTabIndex(index)} index={tabIndex}>
              <TabList mb="10px" borderBottomColor="gray.100" borderBottomWidth="2px">
                <Tab
                  color="#595959"
                  fontWeight="500"
                  pb="4px"
                  fontSize="15px"
                  borderBottomWidth="2px"
                  borderBottomColor="gray.100"
                  _selected={{
                    color: "primary",
                    borderBottomColor: "primary",
                  }}
                >
                  All
                </Tab>
                <Tab
                  fontWeight="500"
                  fontSize="15px"
                  borderBottomWidth="2px"
                  pb="4px"
                  borderBottomColor="gray.100"
                  _selected={{
                    color: "primary",
                    borderBottomColor: "primary",
                  }}
                >
                  Saved
                </Tab>
              </TabList>
              <TabPanels>
                <TabPanel p="0px">
                  <FormControl mb="12px">
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="5px">
                      <FormLabel variant="filter">Loan Status</FormLabel>
                      {selectAllStatus && (
                        <Checkbox
                          onChange={handleAllStatusSelect}
                          isChecked={determineCheckedStatus()}
                        >
                          Select All Status
                        </Checkbox>
                      )}
                    </Box>
                    <MultiSelect
                      isMulti={true}
                      isClearable={false}
                      useBasicStyles
                      placeholder="Select loan status..."
                      selectedOptionColor="black"
                      colorScheme="purple"
                      variant="outline"
                      focusBorderColor={colors.GRAY_50}
                      chakraStyles={selectChakraStyles}
                      value={filters.status || null}
                      onFocus={() => setSelectAllStatus(true)}
                      // onBlur={() => setSelectAll(false)}
                      onChange={(newValue: SelectOption | unknown) =>
                        setFilters({
                          ...filters,
                          status: newValue,
                        })
                      }
                      options={
                        user.type === "borrower"
                          ? user.lender_company.statuses.map((s: any) => ({
                              label: s.label,
                              value: s.id,
                            }))
                          : user.company.statuses.map((s: any) => ({
                              label: s.label,
                              value: s.id,
                            }))
                      }
                      // options={user.company.statuses.map((s: any) => ({
                      //   label: s.label,
                      //   value: s.id,
                      // }))}
                    />
                  </FormControl>
                  <FormControl mb="12px">
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb="5px">
                      <FormLabel variant="filter">Loan Type</FormLabel>
                      {selectAllTypes && (
                        <Checkbox
                          onChange={handleAllTypesSelect}
                          isChecked={determineCheckedTypes()}
                        >
                          Select All Types
                        </Checkbox>
                      )}
                    </Box>
                    <MultiSelect
                      isMulti={true}
                      isClearable={false}
                      useBasicStyles
                      placeholder="Select loan type..."
                      selectedOptionColor="black"
                      colorScheme="purple"
                      variant="outline"
                      focusBorderColor={colors.GRAY_50}
                      onFocus={() => setSelectAllTypes(true)}
                      chakraStyles={selectChakraStyles}
                      value={filters.loan_types || null}
                      onChange={(newValue: SelectOption | unknown) =>
                        setFilters({
                          ...filters,
                          loan_types: newValue,
                        })
                      }
                      options={loanTypes}
                    />
                  </FormControl>

                  <FormControl mb="12px">
                    <FormLabel variant="filter">Date Created</FormLabel>
                    <DateRangeSelect
                      dateRange={filters.created_at}
                      setDateRange={val =>
                        setFilters({
                          ...filters,
                          created_at: val,
                        })
                      }
                    />
                  </FormControl>

                  <FormControl mb="12px">
                    <FormLabel variant="filter">Funding Date</FormLabel>
                    <DateRangeSelect
                      dateRange={filters.funding_date}
                      setDateRange={val =>
                        setFilters({
                          ...filters,
                          funding_date: val,
                        })
                      }
                    />
                  </FormControl>

                  {Object.keys(filters)
                    .filter(key => key !== "loan_types" && key !== "status" && key !== "created_at")
                    .map(field_id => {
                      const field = filters[field_id];

                      if (!field) return null;
                      return (
                        <FormControl mb="12px" key={field.id}>
                          <FormLabel variant="filter">{field.label}</FormLabel>
                          <Flex>
                            <Flex flexGrow="1" alignItems="center">
                              {(field.type === "text" || field.type === "encrypted") && (
                                <Input
                                  variant="primary"
                                  value={field.value}
                                  placeholder="Filter by text"
                                  onChange={ev => setCustomFilter(field, ev.target.value)}
                                />
                              )}
                              {field.type === "date" && (
                                <DateRangeSelect
                                  dateRange={field.value}
                                  setDateRange={val => setCustomFilter(field, val)}
                                />
                              )}
                              {field.type === "dropdown" && (
                                <MultiSelect
                                  isMulti={true}
                                  isClearable={false}
                                  useBasicStyles
                                  placeholder="Choose one or more..."
                                  selectedOptionColor="black"
                                  colorScheme="purple"
                                  variant="outline"
                                  focusBorderColor={colors.GRAY_50}
                                  chakraStyles={selectChakraStyles}
                                  value={field.value}
                                  onChange={(newValue: SelectOption | unknown) =>
                                    setCustomFilter(field, newValue)
                                  }
                                  options={field.options.map((opt: string) => ({
                                    label: opt,
                                    value: opt,
                                  }))}
                                />
                              )}
                              {field.type === "percentage" && (
                                <FilterSlider
                                  field={field}
                                  setField={val => setCustomFilter(field, val)}
                                  formatter={percentFormatter}
                                />
                              )}
                              {field.type === "dollar" && (
                                <FilterSlider
                                  field={field}
                                  setField={val => setCustomFilter(field, val)}
                                  formatter={dollarFormatter}
                                />
                              )}
                              {field.type === "radio" && (
                                <RadioGroup
                                  whiteSpace="nowrap"
                                  ml="20px"
                                  onChange={val => setCustomFilter(field, val)}
                                  value={field.value}
                                >
                                  <Stack direction="row" justifyContent="center" spacing="24px">
                                    {field.options.map((option: string) => (
                                      <Radio
                                        data-cy={option}
                                        whiteSpace="nowrap"
                                        key={option}
                                        value={option}
                                      >
                                        {option}
                                      </Radio>
                                    ))}
                                  </Stack>
                                </RadioGroup>
                              )}
                            </Flex>
                            <IconButton
                              aria-label="remove-filter"
                              variant="ghost"
                              onClick={() => removeCustomFilter(field.id)}
                              icon={<FontAwesomeIcon icon={["far", "times"]} />}
                            />
                          </Flex>
                        </FormControl>
                      );
                    })}
                </TabPanel>
                <SavedFilters setFilters={setFilters} />
              </TabPanels>
            </Tabs>
          </DrawerBody>
          {tabIndex === 0 && (
            <DrawerFooter flexDirection="column" px="0px">
              <Flex w="100%" mb="10px">
                <CompanyFieldsPopover onClick={addCustomFilter} filters={filters} />
              </Flex>
              <HStack
                w="100%"
                px="22px"
                borderTopWidth="1px"
                borderTopColor="gray.50"
                pt="18px"
                spacing="12px"
                justifyContent="space-between"
              >
                <Button variant="outline" colorScheme="red" flex="1" onClick={() => handleCancel()}>
                  Clear
                </Button>
                <SaveFilterButton filters={filters} />
              </HStack>
            </DrawerFooter>
          )}
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default LoanFilters;
